/**
 * GA計測用ヘルパー関数
 *
 * プレコンサポーター機能のGA計測を行うユーティリティ。
 * 現在は主にデバッグ用途で使用（問題特定後に削除予定）。
 */

type GAEventParams = Record<string, string | number | boolean>;

/**
 * GAイベントを送信する
 *
 * @param eventName - イベント名
 * @param params - イベントパラメータ（user_agentは自動付与される）
 */
export const sendGAEvent = (
  eventName: string,
  params: GAEventParams = {}
): void => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, {
      ...params,
      user_agent: navigator.userAgent,
    });
  }
};

/**
 * 動画視聴進捗のステップ
 */
export type VideoProgressStep =
  | 'near_end' // 動画終了0.3秒前
  | 'ended' // endedイベント発火
  | 'save_success' // IndexedDB保存成功
  | 'save_failed' // IndexedDB保存失敗
  | 'ended_timeout'; // endedイベント未発火（タイムアウト）

/**
 * 動画視聴進捗のGA計測パラメータ
 */
export interface VideoProgressParams {
  /** 進捗ステップ */
  step: VideoProgressStep;
  /** 動画URL */
  video_url: string;
  /** 学習進捗ID（同一ユーザー追跡用） */
  progress_id?: string;
  /** 完了済み講座IDのカンマ区切り文字列（空の場合は"none"） */
  completed_lectures?: string;
  /** エラーメッセージ（save_failedの場合のみ） */
  error_message?: string;
}

/**
 * 動画視聴進捗のGA計測イベントを送信する
 *
 * @example
 * // 動画終了0.5秒前
 * sendVideoProgressEvent({
 *   step: 'near_end',
 *   video_url: '/path/to/video.m3u8',
 *   progress_id: 'progress_basic_1234567890',
 *   completed_lectures: 'lecture-001,lecture-002',
 * });
 *
 * // IndexedDB保存失敗
 * sendVideoProgressEvent({
 *   step: 'save_failed',
 *   video_url: '/path/to/video.m3u8',
 *   progress_id: 'progress_basic_1234567890',
 *   completed_lectures: 'none',
 *   error_message: '学習進捗の保存に失敗しました',
 * });
 */
export const sendVideoProgressEvent = (params: VideoProgressParams): void => {
  const eventParams: GAEventParams = {
    step: params.step,
    video_url: params.video_url,
  };

  if (params.progress_id) {
    eventParams.progress_id = params.progress_id;
  }

  if (params.completed_lectures) {
    eventParams.completed_lectures = params.completed_lectures;
  }

  if (params.error_message) {
    eventParams.error_message = params.error_message;
  }

  sendGAEvent('lecture_video_progress', eventParams);
};
